import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { MuiProvider } from './styles/MuiProvider';
import { ThemeModeProvider } from './styles/ThemeModeProvider';
import { BRAND_NAME } from './config/brand';
import { registerApiOptionFetchers } from './runtime/forms/api-option-fetchers';
import { i18nReady } from './i18n';
import { getCatalogText, SUPPORTED_LOCALES } from './i18n/catalog-registry';
import { installRawTranslationKeyDetector } from './i18n/raw-key-detector';
import { browserLogger } from './runtime/logging/browser-logger';
import './styles/globals.css';

/**
 * Application entry point. UI mounts only after the compiled localization
 * runtime is ready, preventing bootstrap key flashes.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

async function bootstrap(): Promise<void> {
  await i18nReady;
  registerApiOptionFetchers();

  if (typeof document !== 'undefined') {
    document.title = BRAND_NAME;
  }

  const root = document.getElementById('root');
  if (!root) throw new Error('Application root element is missing');

  // In dev/test this observes presentation for exact compiled key leakage. It
  // is a no-op in production and is installed before React commits the UI.
  installRawTranslationKeyDetector();

  createRoot(root).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeModeProvider>
          <MuiProvider>
            <App />
          </MuiProvider>
        </ThemeModeProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

void bootstrap().catch((error: unknown) => {
  browserLogger.error('app.bootstrap_failed', 'Application bootstrap failed', {
    error: error instanceof Error ? error.message : String(error),
  });
  const root = typeof document !== 'undefined' ? document.getElementById('root') : null;
  if (root) {
    root.textContent =
      getCatalogText(SUPPORTED_LOCALES.en.catalog, 'common.error.unexpected') ?? BRAND_NAME;
  }
});
